/*
 * @Author: 秦少卫
 * @Date: 2024-07-06 12:34:00
 * @LastEditors: 秦少卫
 * @LastEditTime: 2024-09-26 10:42:54
 * @Description: 图片裁剪插件
 */

import { fabric } from 'fabric';
import _ from 'lodash-es';
import type Editor from '../Editor';
import { v4 as uuid } from 'uuid';

const eventNames = ['sendToBack', 'bringToFront', 'sendBackwards', 'bringForward', 'moveTo'];

eventNames.forEach((eventName) => {
  const oldEvent = fabric.Object.prototype[eventName];
  fabric.Object.prototype[eventName] = function (index: number): fabric.Object {
    const resault = oldEvent.call(this, index);
    this.fire('layer:move');
    return resault;
  };
});

export default class ImgCropPlugin implements IPluginTempl {
  static pluginName = 'ImgCropPlugin';
  static apis = ['cropImage', 'clearCrop', 'findCropImg', 'setCropImg', 'getCorpImgShell'];
  pathObject = null as fabric.Object | null;
  constructor(public canvas: fabric.Canvas, public editor: Editor) {
    this.editor = editor;
    this.canvas = canvas;
    this.pathObject = null;
  }

  // 导入后绑定事件
  async hookImportAfter() {
    this.canvas.getObjects().forEach((item: any, i: number) => {
      if (item.clipId) {
        const shell = this.canvas
          .getObjects()
          .find((info) => info.id === item.clipId) as fabric.Object;

        item.clone((itemCloned: any) => {
          itemCloned.set({
            clipId: item.clipId,
            linkData: item.linkData,
          });

          this.canvas.add(itemCloned);
          itemCloned.moveTo(i);
          this.canvas.remove(item);
          this.bindEvent(shell, itemCloned, itemCloned.clipPath);
        });
      }
    });
  }

  // API 绑定裁剪
  async cropImage(pathObject: fabric.Object, imgUrl: string) {
    const imgObject = await this._loadImg(pathObject, imgUrl);
    const clipPath = await this.cloneClipPaht(pathObject);

    // 初始位置设置
    this._setPosition(pathObject, imgObject, clipPath);

    // 添加元素
    this._addCorpImg(pathObject, imgObject, clipPath);

    // 绑定重多事件
    this.bindEvent(pathObject, imgObject, clipPath);

    // 图层设置
    this._setLayerAndPosition(pathObject, imgObject);

    this.canvas.setActiveObject(pathObject);
    // 选中图片
    // this._selectImg(pathObject, clipPath, imgObject);
  }

  // API 查找图片
  findCropImg(shell: fabric.Object) {
    this.canvas.renderAll();
    const image = this.canvas
      .getObjects()
      .find((info) => info.clipId === shell.id) as fabric.Object;
    return image;
  }

  // API 清空裁剪
  clearCrop(pathObject: fabric.Object) {
    const index = this.canvas.getObjects().findIndex((info) => info === pathObject);
    return new Promise((resolve) => {
      pathObject.clone(async (clonePathObject: fabric.Object) => {
        clonePathObject.set({
          id: pathObject.id,
        });

        // 删除元素
        this.canvas.remove(pathObject);
        const imgObject = this.findCropImg(pathObject);
        if (imgObject) {
          this.canvas.remove(imgObject);
        }
        this.canvas.add(clonePathObject);
        clonePathObject.moveTo(index);
        this.canvas.renderAll();
        resolve(clonePathObject);
      });
    });
  }

  getCorpImgShell() {
    return this.pathObject;
  }
  // API 设置裁剪图片
  async setCropImg(pathObject: fabric.Object, url: string) {
    const newImg = await this._loadImg(pathObject, url);
    const imgObject = this.findCropImg(pathObject);
    imgObject.setSrc(
      url,
      () => {
        imgObject.set({
          ..._.pick(newImg, ['width', 'height', 'angle', 'scaleX', 'scaleY']),
        });
        imgObject.setPositionByOrigin(pathObject.getCenterPoint(), 'center', 'center');
        this._hideBorder(pathObject, imgObject.clipPath);
        this.canvas.renderAll();
        this.pathObject = null;
        this._bindRelationship(pathObject, imgObject);
      },
      { crossOrigin: 'anonymous' }
    );
  }

  _setLayerAndPosition(pathObject: fabric.Object, imgObject: fabric.Object) {
    const index = this.canvas.getObjects().findIndex((info) => info === pathObject);
    imgObject.moveTo(index + 1);
  }

  _addCorpImg(pathObject: fabric.Object, imgObject: fabric.Object, clipPath: fabric.Object) {
    imgObject.set({
      clipId: pathObject.id,
      clipPath: clipPath,
    });
    this.canvas.add(imgObject);
    this.lock(imgObject);
    // this.canvas.discardActiveObject();
  }

  // 拖拽替换图片
  _bingDropEvent(pathObject: fabric.Object, clipPath: fabric.Object, imgObject: fabric.Object) {
    const shwoBorder = () => {
      this.pathObject = pathObject;
      this._showBorder(pathObject, clipPath);
    };
    const hideBorder = () => {
      this.pathObject = null;
      this._hideBorder(pathObject, clipPath);
    };

    const dropHideBorder = () => {
      this._hideBorder(pathObject, clipPath);
    };

    pathObject.on('dragenter', shwoBorder);
    pathObject.on('dragleave', hideBorder);
    pathObject.on('drop', () => dropHideBorder);
    imgObject.on('dragenter', shwoBorder);
    imgObject.on('dragleave', hideBorder);
    imgObject.on('drop', dropHideBorder);

    return () => {
      pathObject.off('dragenter', shwoBorder);
      pathObject.off('dragleave', hideBorder);
      pathObject.off('drop', dropHideBorder);
      imgObject.off('dragenter', shwoBorder);
      imgObject.off('dragleave', hideBorder);
      imgObject.off('drop', dropHideBorder);
    };
  }

  _selectImg(pathObject: fabric.Object, clipPath: fabric.Object, imgObject: fabric.Object) {
    this.unLock(imgObject);
    this.lock(pathObject);
    pathObject.set({
      stroke: 'red',
      strokeWidth: 4,
      paintFirst: 'fill',
    });
    clipPath.set({
      strokeWidth: 4,
    });
    this.canvas.setActiveObject(imgObject).renderAll();
  }

  bindEvent(pathObject: fabric.Object, imgObject: fabric.Object, clipPath: fabric.Object) {
    // 绑定移动
    const offClipPathMoveEvent = this._bindClipPath(pathObject, clipPath);
    const offClipImgMoveEvent = this._bindClipImg(pathObject, imgObject);
    // 绑定图层事件
    const offLayerEvent = this._bindLayer(pathObject, imgObject);
    // 绑定双击
    const offSlectEvent = this._bindSelectEvent(pathObject, imgObject, clipPath);
    // 拖拽事件
    const offDropEvent = this._bingDropEvent(pathObject, clipPath, imgObject);
    // 禁止多选
    const offMultiSelect = this._bindMultiSelect(pathObject, imgObject);

    const offEvents = [
      offClipPathMoveEvent,
      offClipImgMoveEvent,
      offLayerEvent,
      offSlectEvent,
      offDropEvent,
      offMultiSelect,
    ];
    pathObject.on('removed', () => {
      if (imgObject) {
        this.canvas.remove(imgObject);
      }
      offEvents.forEach((off) => off());
    });

    imgObject.on('removed', () => {
      if (pathObject) {
        this.canvas.remove(pathObject);
      }
      offEvents.forEach((off) => off());
    });
  }

  // 禁止元素被多选
  _bindMultiSelect(pathObject: fabric.Object, imgObject: fabric.Object) {
    const notMulit = () => {
      const activeObject = this.canvas.getActiveObject() as fabric.ActiveSelection;
      if (
        activeObject.type === 'activeSelection' &&
        (activeObject.contains(pathObject) || activeObject.contains(imgObject))
      ) {
        activeObject.removeWithUpdate(pathObject);
      }
    };
    this.canvas.on({
      'selection:created': notMulit,
    });

    return () => {
      this.canvas.off({
        'selection:created': notMulit,
      });
    };
  }

  // 创建裁剪区域
  async cloneClipPaht(pathObject: fabric.Object): Promise<fabric.Object> {
    return new Promise((resolve) => {
      pathObject.clone((clonePathObject: fabric.Object) => {
        resolve(clonePathObject);
      });
    });
  }
  // 加载图片
  _loadImg(pathObject: fabric.Object, imgUrl: string): Promise<fabric.Object> {
    return new Promise((resolve) => {
      fabric.Image.fromURL(
        imgUrl,
        (cropImg) => {
          // const scale = fabric.util.findScaleToFit(
          const scale = fabric.util.findScaleToCover(
            {
              width: cropImg.getScaledWidth(),
              height: cropImg.getScaledHeight(),
            },
            {
              width: pathObject.getScaledWidth(),
              height: pathObject.getScaledHeight(),
            }
          );
          cropImg.set({
            id: uuid(),
            scaleX: scale,
            scaleY: scale,
          });
          resolve(cropImg);
        },
        { crossOrigin: 'anonymous' }
      );
    });
  }
  // 裁剪图片
  _bindClipPath = (shell: fabric.Object, clipPath: fabric.Object) => {
    clipPath.set({
      absolutePositioned: true,
    });
    shell.on('moving', () => {
      clipPath.setPositionByOrigin(shell.getCenterPoint(), 'center', 'center');
    });
    shell.on('rotating', () => {
      clipPath.setPositionByOrigin(shell.getCenterPoint(), 'center', 'center');
      clipPath.set({ angle: shell.angle });
    });
    shell.on('scaling', () => {
      clipPath.set({ scaleX: shell.scaleX, scaleY: shell.scaleY });
      clipPath.setPositionByOrigin(shell.getCenterPoint(), 'center', 'center');
    });

    const selectMultipleHander = (e: fabric.IEvent) => {
      if (e.target?._objects) {
        clipPath.setPositionByOrigin(shell.getCenterPoint(), 'center', 'center');
      }
    };
    this.canvas.on('object:moving', selectMultipleHander);

    const offEvent = () => {
      shell.off('moving', () => {
        clipPath.setPositionByOrigin(shell.getCenterPoint(), 'center', 'center');
      });
      shell.off('rotating', () => {
        clipPath.setPositionByOrigin(shell.getCenterPoint(), 'center', 'center');
        clipPath.set({ angle: shell.angle });
      });
      shell.off('scaling', () => {
        clipPath.set({ scaleX: shell.scaleX, scaleY: shell.scaleY });
      });

      this.canvas.off('object:moving', selectMultipleHander);
    };

    return offEvent;
  };

  // bindLayer
  _bindLayer(shell: fabric.Object, cropImg: fabric.Object) {
    const offEvent = () => {
      shell.off('layer:move', () => {
        const index = this.canvas.getObjects().findIndex((info) => info === shell);
        cropImg.moveTo(index + 1);
      });
    };
    shell.on('layer:move', () => {
      const index = this.canvas.getObjects().findIndex((info) => info === shell);
      cropImg.moveTo(index + 1);
      this.canvas.renderAll();
    });

    return offEvent;
  }

  _bindRelationship(shell: fabric.Object, cropImg: fabric.Object) {
    const multiply = fabric.util.multiplyTransformMatrices;
    const invert = fabric.util.invertTransform;
    const bossTransform = shell.calcTransformMatrix();
    const invertedBossTransform = invert(bossTransform);
    const desiredTransform = multiply(invertedBossTransform, cropImg.calcTransformMatrix());
    cropImg.relationship = desiredTransform;
  }
  _bindClipImg(shell: fabric.Object, cropImg: fabric.Object) {
    // const bind = () => {
    //   const bossTransform = shell.calcTransformMatrix();
    //   const invertedBossTransform = invert(bossTransform);
    //   const desiredTransform = multiply(invertedBossTransform, cropImg.calcTransformMatrix());
    //   cropImg.relationship = desiredTransform;
    // };
    const multiply = fabric.util.multiplyTransformMatrices;
    // const invert = fabric.util.invertTransform;
    // bind();

    this._bindRelationship(shell, cropImg);

    const update = () => {
      if (cropImg.relationship) {
        const relationship = cropImg.relationship;
        const newTransform = multiply(shell.calcTransformMatrix(), relationship);
        const opt = fabric.util.qrDecompose(newTransform);
        cropImg.set({
          flipX: false,
          flipY: false,
        });
        cropImg.setPositionByOrigin(
          { x: opt.translateX, y: opt.translateY } as fabric.Point,
          'center',
          'center'
        );
        cropImg.set(opt);
        cropImg.setCoords();
      }
    };

    cropImg.on('deselected', () => this._bindRelationship(shell, cropImg));
    shell.on('moving', update);
    shell.on('rotating', update);
    shell.on('scaling', update);
    const offEvent = () => {
      cropImg.off('deselected', () => this._bindRelationship(shell, cropImg));
      shell.off('moving', update);
      shell.off('rotating', update);
      shell.off('scaling', update);
    };

    return offEvent;
  }

  _bindSelectEvent(shell: fabric.Object, cropImg: fabric.Object, clipPath: fabric.Object) {
    shell.on('mousedblclick', () => {
      this._selectImg(shell, clipPath, cropImg);
      this.canvas.renderAll();
    });

    this.lock(cropImg);
    cropImg.on('deselected', () => {
      this.unLock(shell);
      this.lock(cropImg);

      shell.set({
        stroke: 'red',
        strokeWidth: 0,
        paintFirst: 'fill',
      });
      clipPath.set({
        strokeWidth: 0,
      });
    });

    const offEvent = () => {
      shell.off('mousedblclick', () => {
        this.lock(shell);
        this.unLock(cropImg);
        this.canvas.setActiveObject(cropImg);
      });
      cropImg.off('deselected', () => {
        this.unLock(shell);
        this.lock(cropImg);
      });
    };
    return offEvent;
  }

  _setPosition(pathObject: fabric.Object, imgObject: fabric.Object, clipPath: fabric.Object) {
    clipPath.set({
      left: pathObject.left,
      top: pathObject.top,
    });
    imgObject.setPositionByOrigin(pathObject.getCenterPoint(), 'center', 'center');
  }

  lock(item: fabric.Object) {
    item.set({
      hasControls: false,
      selectable: false,
      evented: false,
    });
  }

  unLock(item: fabric.Object) {
    item.set({
      hasControls: true,
      selectable: true,
      evented: true,
    });
  }

  _showBorder(pathObject: fabric.Object, clipPath: fabric.Object) {
    pathObject.set({
      stroke: 'red',
      strokeWidth: 4,
      paintFirst: 'fill',
    });
    clipPath.set({
      strokeWidth: 4,
    });
    this.canvas.renderAll();
  }
  _hideBorder(pathObject: fabric.Object, clipPath: fabric.Object) {
    pathObject.set({
      stroke: 'red',
      strokeWidth: 0,
      paintFirst: 'fill',
    });
    clipPath.set({
      strokeWidth: 0,
    });
    this.canvas.renderAll();
  }
  destroy() {
    console.log('pluginDestroy');
  }
}
